import React from "react";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import ViewSidebarOutlinedIcon from "@mui/icons-material/ViewSidebarOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import TravelExploreRoundedIcon from "@mui/icons-material/TravelExploreRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import { useLocation, Link } from "react-router-dom";

import {
  dashboardShell,
  DRAWER_WIDTH,
  DRAWER_WIDTH_COLLAPSED,
  SIDEBAR_TRANSITION_MS,
} from "../../theme/dashboardShell";
import TokenitizeName from "../layout/TokenitizeName";
import SarIcon from "../icons/SarIcon";
import { Layers } from "@mui/icons-material";

const { sidebar: shell } = dashboardShell;

const NAV = [
  { label: "Dashboard", to: "/investor/dashboard", icon: <DashboardRoundedIcon />, end: true },
  { label: "View & Invest", to: "/investor/viewinvest", icon: <TravelExploreRoundedIcon /> },
  { label: "Staking", to: "/investor/staking", icon: <Layers /> },
  { label: "Mortgage Loan", to: "/investor/mortgage", icon: <AccountBalanceRoundedIcon /> },
  { label: "Income", to: "/investor/income", icon: <SarIcon /> },
  { label: "Settings", to: "/investor/settings", icon: <SettingsRoundedIcon /> },
];

function SidebarContent({ collapsed, onToggleCollapse, onItemClick, showCollapseToggle = true }) {
  const { pathname } = useLocation();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: shell.bg,
        color: shell.text,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: collapsed && showCollapseToggle ? "column" : "row",
          alignItems: "center",
          justifyContent: collapsed && showCollapseToggle ? "center" : "space-between",
          gap: collapsed && showCollapseToggle ? 0.5 : 0,
          px: collapsed && showCollapseToggle ? 0.75 : 1.5,
          py: 1.25,
          bgcolor: shell.bgHeader,
          borderBottom: shell.border,
          minHeight: 56,
        }}
      >
        {(!collapsed || !showCollapseToggle) && (
          <TokenitizeName height={32} sx={{ flex: 1, minWidth: 0 }} />
        )}
        {showCollapseToggle ? (
          <IconButton
            size="small"
            onClick={onToggleCollapse}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!collapsed}
            sx={{
              color: shell.textMuted,
              flexShrink: 0,
              border: "1px solid rgba(148, 163, 184, 0.25)",
              borderRadius: 1,
              "&:hover": { bgcolor: "rgba(148, 163, 184, 0.12)", color: shell.text },
            }}
          >
            {collapsed ? (
              <ChevronRightIcon fontSize="small" />
            ) : (
              <ViewSidebarOutlinedIcon sx={{ fontSize: 20 }} />
            )}
          </IconButton>
        ) : null}
      </Box>

      {(!collapsed || !showCollapseToggle) && (
        <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              color: shell.textMuted,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              fontWeight: 600,
              fontSize: 11,
            }}
          >
            Main menu
          </Typography>
        </Box>
      )}

      <List sx={{ px: collapsed && showCollapseToggle ? 0.75 : 1, py: 1, flex: 1 }}>
        {NAV.map((item) => {
          const active = item.end
            ? pathname === item.to
            : pathname === item.to ||
              pathname.startsWith(`${item.to}/`) ||
              (item.to === "/investor/viewinvest" && pathname === "/investor/secondary-market");
          const button = (
            <ListItemButton
              key={item.label}
              component={Link}
              to={item.to}
              onClick={onItemClick}
              sx={{
                mb: collapsed && showCollapseToggle ? 0.75 : 1.25,
                borderRadius:
                  collapsed && showCollapseToggle ? shell.navItemRadiusCollapsed : shell.navItemRadiusExpanded,
                px: collapsed && showCollapseToggle ? 0 : 1.5,
                py: collapsed && showCollapseToggle ? 1 : 1.1,
                justifyContent: collapsed && showCollapseToggle ? "center" : "flex-start",
                minHeight: shell.navItemMinHeight,
                boxShadow: active ? shell.itemActiveShadow : "none",
                border: shell.itemActiveBorder,
                bgcolor: active ? shell.itemActive : "transparent",
                color: active ? shell.itemActiveText : shell.textMuted,
                transition: (t) => t.transitions.create(["background-color", "color"], { duration: 160 }),
                "&:hover": {
                  bgcolor: active ? shell.itemActiveHover : shell.itemHover,
                  boxShadow: active ? shell.itemActiveShadow : "none",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed && showCollapseToggle ? 0 : 36,
                  justifyContent: "center",
                  color: active ? shell.iconActive : shell.iconMuted,
                  "& .MuiSvgIcon-root": { fontSize: 22 },
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!(collapsed && showCollapseToggle) && (
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: active ? 600 : 500,
                    color: active ? shell.itemActiveText : shell.textMuted,
                    letterSpacing: 0.01,
                  }}
                />
              )}
            </ListItemButton>
          );

          if (collapsed && showCollapseToggle) {
            return (
              <Tooltip key={item.label} title={item.label} placement="right" arrow>
                <Box component="span" sx={{ display: "block" }}>
                  {button}
                </Box>
              </Tooltip>
            );
          }
          return button;
        })}
      </List>

      <Box sx={{ flexGrow: 1 }} />
    </Box>
  );
}

export default function InvestorSidebar({ open, onClose, collapsed = false, onToggleCollapse }) {
  const width = collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  return (
    <>
      <Box
        component="nav"
        aria-label="Investor main menu"
        sx={{
          width,
          height: "100dvh",
          position: "fixed",
          left: 0,
          top: 0,
          zIndex: 1200,
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          borderRight: shell.border,
          bgcolor: shell.bg,
          transition: (t) =>
            t.transitions.create("width", {
              easing: t.transitions.easing.sharp,
              duration: SIDEBAR_TRANSITION_MS,
            }),
          overflow: "hidden",
        }}
      >
        <SidebarContent
          collapsed={collapsed}
          onToggleCollapse={onToggleCollapse}
          showCollapseToggle
        />
      </Box>

      <Drawer
        variant="temporary"
        anchor="left"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "flex", md: "none" },
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            border: "none",
            backgroundColor: shell.bg,
            borderRight: shell.border,
          },
        }}
      >
        <SidebarContent
          collapsed={false}
          onToggleCollapse={onToggleCollapse}
          onItemClick={onClose}
          showCollapseToggle={false}
        />
      </Drawer>
    </>
  );
}
